import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { portfolioStore } from '../../stores/portfolio-store';
import { Button, DataGrid } from '@privemind/components';
import { AddAssetModal } from '../add-asset-modal/add-asset-modal';
import { EditAssetModal } from '../edit-asset-modal/edit-asset-modal';
import { DeleteAssetModal } from '../delete-asset-modal/delete-asset-modal';
import { AssetDisplayData } from '../../types';
import { formatCurrency } from '@privemind/utils/formatting';
import './bucket-detail.scss';

export const BucketDetail: React.FC = observer(() => {
  const { bucketName } = useParams<{ bucketName: string }>();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetDisplayData | null>(null);

  if (!bucketName || !portfolioStore.bucketExists(bucketName)) {
    return (
      <div className="bucket-detail">
        <div className="bucket-detail__error">
          <h2>Bucket not found</h2>
          <p>The bucket "{bucketName}" does not exist.</p>
          <Button onClick={() => navigate('/edit-assets')}>
            Back to Edit Assets
          </Button>
        </div>
      </div>
    );
  }

  const bucketData = portfolioStore.bucketDisplayData.find(b => b.name === bucketName);

  const handleDeleteBucket = () => {
    if (window.confirm(`Delete bucket "${bucketName}" and all its assets?`)) {
      portfolioStore.removeBucket(bucketName);
      navigate('/edit-assets');
    }
  };

  const handleBackClick = () => {
    navigate('/edit-assets');
  };

  const handleEditAsset = (asset: AssetDisplayData) => {
    setSelectedAsset(asset);
    setEditModalOpen(true);
  };

  const handleDeleteAsset = (asset: AssetDisplayData) => {
    setSelectedAsset(asset);
    setDeleteModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedAsset(null);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedAsset(null);
  };

  return (
    <div className="bucket-detail">
      <div className="bucket-detail__header">
        <div className="bucket-detail__title-section">
          <Button 
            color="secondary" 
            onClick={handleBackClick}
            className="bucket-detail__back-button"
          >
            ← Back to Buckets
          </Button>
          <h1 className="bucket-detail__title">{bucketName}</h1>
        </div>
        
        <div className="bucket-detail__actions">
          <Button onClick={() => setIsModalOpen(true)}>
            Add Asset
          </Button>
          <Button color="danger" onClick={handleDeleteBucket}>
            Delete Bucket
          </Button>
        </div>
      </div>

      {bucketData && (
        <div className="bucket-detail__stats">
          <div className="bucket-detail__stat-card">
            <span className="bucket-detail__stat-label">Total Value</span>
            <span className="bucket-detail__stat-value">
              {formatCurrency(bucketData.value)}
            </span>
          </div>
          
          <div className="bucket-detail__stat-card">
            <span className="bucket-detail__stat-label">Total Assets</span>
            <span className="bucket-detail__stat-value">
              {bucketData.assets.length}
            </span>
          </div>
          
          <div className="bucket-detail__stat-card">
            <span className="bucket-detail__stat-label">Portfolio Allocation</span>
            <span className="bucket-detail__stat-value">
              {bucketData.allocation.toFixed(1)}%
            </span>
          </div>
        </div>
      )}

      <div className="bucket-detail__content">
        <h2>Assets</h2>
        
        {bucketData && (
          <DataGrid
            rows={bucketData.assets}
            columns={[
              {
                id: 'name',
                header: 'Asset',
                render: (asset) => (
                  <div>
                    <div>{asset.name}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>{asset.symbol.toUpperCase()}</div>
                  </div>
                )
              },
              {
                id: 'amount',
                header: 'Amount',
                render: (asset) => asset.amount.toFixed(8)
              },
              {
                id: 'marketValue',
                header: 'Market Value',
                render: (asset) => formatCurrency(asset.marketValue)
              },
              {
                id: 'pnl',
                header: 'P&L',
                render: (asset) => (
                  <span style={{ color: asset.pnl >= 0 ? '#10b981' : '#ef4444' }}>
                    {formatCurrency(asset.pnl)}
                  </span>
                )
              },
              {
                id: 'actions',
                header: 'Actions',
                render: (asset) => (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button size="sm" color="secondary" onClick={() => handleEditAsset(asset)}>
                      Edit
                    </Button>
                    <Button size="sm" color="danger" onClick={() => handleDeleteAsset(asset)}>
                      Delete
                    </Button>
                  </div>
                )
              }
            ]}
          />
        )}
      </div>

      {isModalOpen && (
        <AddAssetModal
          bucketName={bucketName}
          isOpen={true}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {selectedAsset && portfolioStore.getAsset(selectedAsset.id) && (
        <>
          <EditAssetModal
            asset={portfolioStore.getAsset(selectedAsset.id)!}
            assetId={selectedAsset.id}
            isOpen={editModalOpen}
            onClose={handleCloseEditModal}
          />
          <DeleteAssetModal
            asset={portfolioStore.getAsset(selectedAsset.id)!}
            assetId={selectedAsset.id}
            isOpen={deleteModalOpen}
            onClose={handleCloseDeleteModal}
          />
        </>
      )}
    </div>
  );
});
